import React, { useEffect, useRef, useState } from 'react'

import * as styles from './WistiaEmbed.module.scss'

// Add this to make TypeScript happy with the _wq property on window
declare global {
  interface Window {
    _wq: any
  }
}
interface IWistiaPlayerProps {
  abTest?: boolean
  noMargin?: boolean
  videoId: string
  lazyLoad?: boolean
}

/**
 * WistiaPlayer
 *
 * @description
 * Use this if you are embedding a video using Wistia's AB Testing feature
 *
 * @usage
 * <WistiaPlayer abTest={true} videoId='74r6lcgbvs' />
 */

export const WistiaPlayer = ({
  abTest,
  noMargin = false,
  videoId,
  lazyLoad = false
}: IWistiaPlayerProps) => {
  const videoContainer = useRef<HTMLDivElement | null>(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState(!lazyLoad) // Default to true if not lazy loading
  const [isPlayerReady, setIsPlayerReady] = useState(false)

  // Load Wistia script once if not already loaded
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Only load the script once
    if (
      !document.querySelector(
        'script[src*="fast.wistia.com/assets/external/E-v1.js"]'
      )
    ) {
      const script = document.createElement('script')
      script.src = 'https://fast.wistia.com/assets/external/E-v1.js'
      script.async = true
      script.onload = () => {
        setIsPlayerReady(true)
      }
      document.body.appendChild(script)
    } else {
      setIsPlayerReady(true)
    }
  }, [])

  // Lazy loading observer
  useEffect(() => {
    if (!lazyLoad) return // If not lazy loading, skip this effect

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setIsVideoLoaded(true)
          observer.disconnect()
        }
      }
    }

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '100px', // Load a bit before it comes into view
      threshold: 0.1
    })

    if (videoContainer.current) {
      observer.observe(videoContainer.current)
    }

    return () => observer.disconnect()
  }, [lazyLoad])

  // Initialize the player when both conditions are met
  useEffect(() => {
    if (!isVideoLoaded || !isPlayerReady || !videoContainer.current) return

    // Clear any existing content to prevent duplicates
    if (videoContainer.current.querySelector('.wistia_embed')) {
      return // Player already initialized
    }

    // Create the div for Wistia to target
    const playerDiv = document.createElement('div')
    playerDiv.className = `wistia_embed wistia_async_${videoId} videoFoam=true`

    // Clear container and append new div
    videoContainer.current.innerHTML = ''
    videoContainer.current.appendChild(playerDiv)

    // Initialize player
    if (window._wq) {
      window._wq = window._wq || []
      window._wq.push({
        id: videoId,
        onReady: (video: any) => {
          // Video is ready to play
          console.log('Wistia video ready')
        }
      })
    }
  }, [isVideoLoaded, isPlayerReady, videoId])

  return (
    <div
      ref={videoContainer}
      className={styles.wistiaEmbed + ' ' + (noMargin ? styles.noMargin : '')}
    ></div>
  )
}
